import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  getAllBulkImports,
  getBulkImportTypes,
  UserKeys,
} from '../../../api/users-api';
import { BulkImportModel, BulkImportType } from '../../../../types';
import SimpleDrawer, {
  SimpleDrawerAction,
} from '../../../shared/simple-drawer-new';
import SharedReviewLayoutMainCard, {
  MainSectionItem,
} from '../../../shared/shared-review-layout-main-card';
import SharedReviewLayoutNew, {
  ReviewSection,
} from '../../../shared/shared-review-layout-new';
import {
  ReviewSectionItem,
  TargetType,
} from '../../../shared/shared-review-layout-item';
import AvatarChip from '../../../shared/avatar-chip';
import HtmlPreview from '../../../shared/html-preview';
import { useBulkOpActions } from './hooks/use-bulk-op-actions';
import { useUserSettings } from '../../../utils/use-user-settings';
import { isAbleToSeeUIElement } from '../../../utils/roles/client-roles';
import DeleteBulkOpsDialog from './delete-bulk-ops-dialog';
import RefreshBulkOpsDialog from './refresh-bulk-ops-dialog';
import RunBulkOpsDialog from './run-bulk-ops-dialog';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import dayjs from 'dayjs';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import Check from '@mui/icons-material/Check';
import Delete from '@mui/icons-material/Delete';
import FileDownload from '@mui/icons-material/FileDownload';
import Pageview from '@mui/icons-material/Pageview';
import PlayArrow from '@mui/icons-material/PlayArrow';
import RestorePage from '@mui/icons-material/RestorePage';
import Update from '@mui/icons-material/Update';

interface BulkOpsDrawerContentProps {
  selectedBulkOp: BulkImportModel;
  bulkImportTypes: BulkImportType[];
}

const sourceChipTypeToTargetType = (
  sourceType: BulkImportModel['sourceType']
): TargetType => {
  switch (sourceType) {
    case 'User':
      return 'User';
    case 'Group':
      return 'Group';
    case 'OrgUnit':
      return 'OrgUnit';
    case 'Domain':
      return 'Domain';
    case 'gPanel_Role':
      return 'gPanel Role';
    default:
      return 'Unknown';
  }
};

const formatSourceType = (
  sourceType: BulkImportModel['sourceType']
): string => {
  switch (sourceType) {
    case 'All_Users':
      return 'All Users';
    case 'All_Groups':
      return 'All Groups';
    case 'gPanel_Role':
      return 'gPanel Role';
    case 'Mobile_Device':
      return 'Mobile Device';
    case null:
      return 'N/A';
    default:
      return sourceType;
  }
};

export function BulkOpsDrawerContent({
  selectedBulkOp,
  bulkImportTypes,
}: Readonly<BulkOpsDrawerContentProps>) {
  const theme = useTheme();
  const [showAllColumns, setShowAllColumns] = useState(false);
  const MAX_CHIPS = Math.min(
    5,
    Math.max(4, Math.ceil((selectedBulkOp.columns?.length ?? 0) / 2))
  );

  const bulkImportType = bulkImportTypes.find(
    (type) => type.name === selectedBulkOp.bulkImport
  );

  const mainSection: MainSectionItem = useMemo(() => {
    return {
      mainTitle: selectedBulkOp.name || 'N/A',
      description: selectedBulkOp.description || 'None Provided',
      status: selectedBulkOp.status || 'IDLE',
      fields: [
        {
          label: 'Last Run',
          value: selectedBulkOp.lastRan
            ? dayjs(selectedBulkOp.lastRan).format('MMM D, YYYY, h:mm A')
            : 'Never Ran',
          icon: Update,
        },
        {
          label: 'Created Date',
          value: selectedBulkOp.createdDate
            ? dayjs(selectedBulkOp.createdDate).format('MMM D, YYYY, h:mm A')
            : 'N/A',
          icon: Check,
        },
        ...(bulkImportType?.usesSpreadsheet
          ? [
              {
                label: 'Last Export Date',
                value: selectedBulkOp.lastExportUpdate
                  ? dayjs(selectedBulkOp.lastExportUpdate).format(
                      'MMM D, YYYY, h:mm A'
                    )
                  : 'Not Yet Exported',
                icon: FileDownload,
              },
            ]
          : []),
      ],
    };
  }, [selectedBulkOp, bulkImportType]);

  const leftSections: ReviewSection[] = useMemo(() => {
    const sections: ReviewSection[] = [];

    // Basic Information Section
    const basicItems: ReviewSectionItem[] = [
      {
        label: 'Created By:',
        value: selectedBulkOp.createdBy ? (
          <AvatarChip
            email={selectedBulkOp.createdBy}
            type="User"
            label={selectedBulkOp.createdBy}
          />
        ) : (
          'N/A'
        ),
      },
      {
        label: 'Bulk Import Type:',
        value: bulkImportType?.niceName || selectedBulkOp.bulkImport || 'N/A',
      },
    ];

    if (selectedBulkOp.notifyEmails?.length) {
      basicItems.push({
        label: 'Email Notifications:',
        value: selectedBulkOp.notifyEmails.map((email) => ({
          email,
          type: 'User' as TargetType,
        })),
        variant: 'chip',
        flex:
          Array.isArray(selectedBulkOp.notifyEmails) &&
          selectedBulkOp.notifyEmails.length > 1,
      });
    }

    sections.push({
      title: 'Basic Information',
      leftWidth: { labelWidth: 5, valueWidth: 7 },
      leftItems: basicItems,
    });

    // Configuration Section
    const configItems: ReviewSectionItem[] = [];
    if (selectedBulkOp.path) {
      configItems.push({
        label: 'Path:',
        value:
          selectedBulkOp.path === 'guideMe'
            ? 'Guided Export'
            : 'Advanced Operation',
      });
    }

    if (selectedBulkOp.spreadsheetId) {
      configItems.push({
        label: 'Spreadsheet Id:',
        value: selectedBulkOp.spreadsheetId,
      });
    }

    if (selectedBulkOp.resourceIds?.length) {
      configItems.push({
        label: 'Resource Ids:',
        value: selectedBulkOp.resourceIds.map((resourceId) => ({
          email: resourceId,
          type: 'Unknown' as TargetType,
        })),
        variant: 'chip',
        flex:
          Array.isArray(selectedBulkOp.resourceIds) &&
          selectedBulkOp.resourceIds.length > 1,
      });
    }

    if (selectedBulkOp.startDate) {
      configItems.push({
        label: 'Start Date:',
        value: dayjs(selectedBulkOp.startDate).format('MM/DD/YYYY'),
      });
    }

    if (selectedBulkOp.endDate) {
      configItems.push({
        label: 'End Date:',
        value: dayjs(selectedBulkOp.endDate).format('MM/DD/YYYY'),
      });
    }

    if (configItems.length > 0) {
      sections.push({
        title: 'Configuration',
        leftWidth: { labelWidth: 5, valueWidth: 7 },
        leftItems: configItems,
      });
    }

    // Columns Section
    if (selectedBulkOp.columns?.length) {
      const columnChips = (
        <Stack gap={1}>
          <Stack direction="row" gap={1} flexWrap="wrap">
            {selectedBulkOp.columns
              .slice(
                0,
                showAllColumns ? selectedBulkOp.columns.length : MAX_CHIPS
              )
              .map((col) => (
                <AvatarChip
                  key={col.columnName}
                  email={col.columnName}
                  type="Unknown"
                />
              ))}
          </Stack>
          {selectedBulkOp.columns.length > MAX_CHIPS && (
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                justifyContent: 'flex-start',
              }}
            >
              <Button
                size="small"
                onClick={() => setShowAllColumns(!showAllColumns)}
                sx={{ color: theme.palette.semantic?.hyperlink?.active }}
              >
                {showAllColumns
                  ? 'Show less'
                  : `+${selectedBulkOp.columns.length - MAX_CHIPS} more`}
              </Button>
            </Box>
          )}
        </Stack>
      );

      sections.push({
        title: 'Columns',
        leftWidth: { labelWidth: 5, valueWidth: 7 },
        leftItems: [
          {
            label: 'Selected Columns:',
            value: columnChips,
            flex:
              Array.isArray(selectedBulkOp.columns) &&
              selectedBulkOp.columns.length > 1,
          },
        ],
      });
    }

    // Sources Section
    const sourceItems: ReviewSectionItem[] = [];
    if (selectedBulkOp.sourceType) {
      sourceItems.push({
        label: 'Source Type:',
        value: formatSourceType(selectedBulkOp.sourceType),
      });
    }

    if (selectedBulkOp.allSource) {
      sourceItems.push({
        label: 'Sources:',
        value: 'All',
      });
    } else if (selectedBulkOp.sources?.length) {
      const chipType = sourceChipTypeToTargetType(selectedBulkOp.sourceType);
      const sourcesValues = selectedBulkOp.sources;
      sourceItems.push({
        label: 'Sources:',
        value: sourcesValues.map((source) => ({
          email: source,
          type: chipType,
        })),
        variant: 'chip',
        flex: Array.isArray(sourcesValues) && sourcesValues.length > 1,
      });
    }

    if (sourceItems.length > 0) {
      sections.push({
        title: 'Sources',
        leftWidth: { labelWidth: 5, valueWidth: 7 },
        leftItems: sourceItems,
      });
    }

    // Group Configuration Section
    const groupItems: ReviewSectionItem[] = [];
    const nestedGroups = selectedBulkOp.includeNestedGroups || [];
    if (nestedGroups.length > 0) {
      groupItems.push({
        label: 'Included Nested Groups:',
        value: nestedGroups.map((group) => ({
          email: group,
          type: 'Group' as TargetType,
        })),
        variant: 'chip',
        flex: Array.isArray(nestedGroups) && nestedGroups.length > 1,
      });
    }

    if (selectedBulkOp.bulkImport === 'groupImport') {
      groupItems.push({
        label: 'Auto Create Groups:',
        value: Boolean(selectedBulkOp.createGroups),
      });
    }

    if (groupItems.length > 0) {
      sections.push({
        title: 'Group Configuration',
        leftWidth: { labelWidth: 5, valueWidth: 7 },
        leftItems: groupItems,
      });
    }

    // Vacation Responder Section
    if (
      selectedBulkOp.bulkImport === 'usersVacationResponder' &&
      selectedBulkOp.vacationResponderData
    ) {
      const vacation = selectedBulkOp.vacationResponderData;
      const vacationItems: ReviewSectionItem[] = [];

      if (vacation.startTime) {
        vacationItems.push({
          label: 'Start Date:',
          value: dayjs(vacation.startTime).format('MM/DD/YYYY'),
        });
      }
      if (vacation.endTime) {
        vacationItems.push({
          label: 'End Date:',
          value: dayjs(vacation.endTime).format('MM/DD/YYYY'),
        });
      }
      if (vacation.responseSubject) {
        vacationItems.push({
          label: 'Subject:',
          value: vacation.responseSubject,
        });
      }
      if (vacation.responseBodyHtml) {
        vacationItems.push({
          label: 'Preview:',
          value: (
            <HtmlPreview
              title="Vacation Responder Preview"
              html={vacation.responseBodyHtml}
              showTitle={false}
            />
          ),
          flex: true,
        });
      }

      if (vacationItems.length > 0) {
        sections.push({
          title: 'Vacation Responder',
          leftWidth: { labelWidth: 5, valueWidth: 7 },
          leftItems: vacationItems,
        });
      }
    }

    return sections;
  }, [selectedBulkOp, bulkImportType, showAllColumns, MAX_CHIPS]);

  return (
    <Stack spacing={2} sx={{ width: '100%' }}>
      <SharedReviewLayoutMainCard section={mainSection} isDrawer={true} />
      <SharedReviewLayoutNew leftSections={leftSections} isDrawer={true} />
    </Stack>
  );
}

/**
 * Global, self-contained bulk operation detail drawer.
 * Driven entirely by the `?selectedBulkOp=<id>` URL search param.
 * Place this once at the App level — no props required.
 */
export default function BulkOpsDrawerDetails() {
  const [searchParams] = useSearchParams();
  const {
    closeDrawer,
    hasBulkOpsPermission,
    getPermissionDisabledReason,
    handleViewSpreadsheet,
    runDirectBulkOp,
    isRunningBulkOp,
    changeLogRoles,
  } = useBulkOpActions();
  const { toolsRoles, loggedInUser } = useUserSettings();

  const selectedBulkOpId = searchParams.get('selectedBulkOp');

  const { data: bulkImportModels, isLoading: isLoadingModels } = useQuery({
    queryKey: [UserKeys.BulkOps],
    queryFn: () => getAllBulkImports(),
    enabled: !!selectedBulkOpId,
  });

  const { data: bulkImportTypes, isLoading: isLoadingTypes } = useQuery({
    queryKey: [UserKeys.BulkOpsTypes],
    queryFn: () => getBulkImportTypes(),
    enabled: !!selectedBulkOpId,
  });

  const selectedBulkOp =
    bulkImportModels?.find((m) => m.id.toString() === selectedBulkOpId) ?? null;

  const notFound = !!selectedBulkOpId && !isLoadingModels && !selectedBulkOp;

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openRefreshDialog, setOpenRefreshDialog] = useState(false);
  const [openRunDialog, setOpenRunDialog] = useState(false);
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const bulkImportType = bulkImportTypes?.find(
    (type) => type.name === selectedBulkOp?.bulkImport
  );

  const canDelete = hasBulkOpsPermission(toolsRoles);
  const refreshDisabledReason = getPermissionDisabledReason(
    toolsRoles,
    bulkImportType?.role || ''
  );
  const canRefresh = !refreshDisabledReason;
  const canRefreshSpreadsheet =
    selectedBulkOp?.path === 'guideMe' && bulkImportType?.usesSpreadsheet;

  const runPermissionDisabledReason = getPermissionDisabledReason(
    toolsRoles,
    bulkImportType?.role || ''
  );
  const runDisabledReason = runPermissionDisabledReason
    ? runPermissionDisabledReason
    : selectedBulkOp?.status === 'RUNNING'
    ? 'This bulk op is currently running.'
    : undefined;
  const canRun = !runDisabledReason;

  const handleRunBulkOp = () => {
    if (!selectedBulkOp) return;
    if (bulkImportType?.usesSpreadsheet) {
      setOpenRunDialog(true);
    } else if (
      isAbleToSeeUIElement(changeLogRoles, bulkImportType?.role || '')
    ) {
      setOpenChangeLogDialog(true);
    } else {
      runDirectBulkOp({ bulkOp: selectedBulkOp, changeLogMessage: '' });
    }
  };

  const actionsMenu: SimpleDrawerAction[] | undefined = selectedBulkOp
    ? [
        {
          label: 'Run',
          icon:
            isRunningBulkOp || selectedBulkOp.status === 'RUNNING' ? (
              <CircularProgress size={20} />
            ) : (
              <PlayArrow fontSize="small" />
            ),
          disabled: !canRun || isRunningBulkOp,
          disabledReason: runDisabledReason,
          onClick: handleRunBulkOp,
        },
        ...(bulkImportType?.usesSpreadsheet
          ? [
              {
                label: 'View Spreadsheet',
                icon: <Pageview fontSize="small" />,
                disabled: false,
                onClick: () => {
                  handleViewSpreadsheet(selectedBulkOp, loggedInUser);
                },
              },
            ]
          : []),
        ...(canRefreshSpreadsheet
          ? [
              {
                label: 'Refresh Spreadsheet Data',
                icon: <RestorePage fontSize="small" />,
                disabled: !canRefresh,
                disabledReason: refreshDisabledReason,
                onClick: () => {
                  setOpenRefreshDialog(true);
                },
              },
            ]
          : []),
        {
          label: 'Delete',
          icon: <Delete fontSize="small" />,
          disabled: !canDelete,
          disabledReason: canDelete
            ? undefined
            : 'You do not have the required permission(s) to delete this bulk operation.',
          onClick: () => {
            setOpenDeleteDialog(true);
          },
        },
      ]
    : undefined;

  return (
    <>
      <SimpleDrawer
        title="Bulk Operation Details"
        open={!!selectedBulkOpId}
        onClose={closeDrawer}
        isLoading={isLoadingModels || isLoadingTypes}
        actionsMenu={actionsMenu}
      >
        {selectedBulkOp && (
          <BulkOpsDrawerContent
            selectedBulkOp={selectedBulkOp}
            bulkImportTypes={bulkImportTypes || []}
          />
        )}
        {notFound && (
          <Typography sx={{ p: 3 }} color="error">
            Bulk operation is not found.
          </Typography>
        )}
      </SimpleDrawer>

      {openDeleteDialog && selectedBulkOp && (
        <DeleteBulkOpsDialog
          open={openDeleteDialog}
          handleClose={() => setOpenDeleteDialog(false)}
          selectedBulkOps={[selectedBulkOp]}
          onDeleteSuccess={closeDrawer}
        />
      )}

      {openRefreshDialog && selectedBulkOp && (
        <RefreshBulkOpsDialog
          open={openRefreshDialog}
          handleClose={() => setOpenRefreshDialog(false)}
          selectedBulkOp={selectedBulkOp}
        />
      )}

      {openRunDialog && selectedBulkOp && (
        <RunBulkOpsDialog
          open={openRunDialog}
          handleClose={() => setOpenRunDialog(false)}
          selectedBulkOp={selectedBulkOp}
          loggedInUserEmail={loggedInUser}
          bulkImportTypes={bulkImportTypes || []}
          changeLogRoles={changeLogRoles}
        />
      )}

      {selectedBulkOp && (
        <ChangeLogDialog
          open={openChangeLogDialog}
          handleClose={() => setOpenChangeLogDialog(false)}
          changeLogMessage={changeLogMessage}
          setChangeLogMessage={setChangeLogMessage}
          mutate={() => {
            runDirectBulkOp({ bulkOp: selectedBulkOp, changeLogMessage });
            setOpenChangeLogDialog(false);
            setChangeLogMessage('');
          }}
          mutateLoading={isRunningBulkOp}
        />
      )}
    </>
  );
}
